import { useMemo } from 'react'
import {
  useAudioNoteActiveNoteId,
  useAudioNoteMeetingCalendarEventId,
  useSavedAudioNoteId,
} from '@renderer/hooks/audio/use-audio-note-selectors'
import type { MeetingViewContext } from '../../views/meeting-view/use-meeting-view-model'

/**
 * Which meeting is being recorded right now, as Meeting View context — ready to
 * hand straight to `openView(MEETING_VIEW_ID, …)`.
 *
 * Every entry point that opens the live meeting (Mini, the audio pill, the
 * toolbar, a recording meeting cell) has to say *which* meeting it's opening:
 * the router dedupes on identity, so a context-less open reads as a different
 * document from the same meeting already open by calendar event or note id, and
 * stacks a second Meeting View.
 *
 * The saved-note id is the tail of the live session — the active id is nulled
 * at save time while Mini is still showing the finished meeting.
 */
export function useLiveMeetingIdentity(): MeetingViewContext {
  const activeNoteId = useAudioNoteActiveNoteId()
  const savedNoteId = useSavedAudioNoteId()
  const calendarEventId = useAudioNoteMeetingCalendarEventId()

  return useMemo(
    () => ({
      mode: 'live',
      ...(activeNoteId || savedNoteId ? { audioNoteId: activeNoteId ?? savedNoteId ?? undefined } : {}),
      ...(calendarEventId ? { calendarEventId } : {}),
    }),
    [activeNoteId, savedNoteId, calendarEventId],
  )
}
